import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { transactionService, reviewService } from '../services/api';
import TransactionChat from '../components/TransactionChat';

interface TransactionReview {
  id: number;
  transactionId: number;
  reviewerId: number;
  revieweeId: number;
  rating: number;
  comment?: string | null;
  createdAt: string;
  reviewer?: {
    id: number;
    displayName: string;
    profilePicture?: string;
  };
  reviewee?: {
    id: number;
    displayName: string;
    profilePicture?: string;
  };
}

interface Transaction {
  id: number;
  listingId: number;
  buyerId: number;
  sellerId: number;
  agreedPrice: number;
  shippingCost?: number;
  status: string;
  sellerProofImage?: string;
  paymentSlipUrl?: string;
  shippingProofUrl?: string;
  shippingTrackingNumber?: string;
  buyerNotes?: string;
  sellerNotes?: string;
  finalPrice?: number;
  createdAt: string;
  updatedAt: string;
  listing: {
    id: number;
    imageUrls: string;
    isBundle?: boolean;
    bundleName?: string;
    bundleItems?: any;
    listingType?: 'WTS' | 'WTB';
    customCardName?: string;
    card?: {
      id: number;
      name: string;
      cardNumber: string;
      imageUrl: string;
    };
    seller: {
      id: number;
      displayName: string;
      email: string;
      profilePicture?: string;
      rating?: number;
      reviewCount?: number;
    };
  };
  buyer: {
    id: number;
    displayName: string;
    email: string;
    profilePicture?: string;
    rating?: number;
    reviewCount?: number;
  };
  seller?: {
    id: number;
    displayName: string;
    email: string;
    profilePicture?: string;
    rating?: number;
    reviewCount?: number;
  };
  reviews?: TransactionReview[];
}

const TransactionDetailPage = () => {

  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadingPayment, setUploadingPayment] = useState(false);
  const [uploadingShipping, setUploadingShipping] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [confirmingReceipt, setConfirmingReceipt] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [sellerNotes, setSellerNotes] = useState('');
  const [shippingFile, setShippingFile] = useState<File | null>(null);
  const [paymentFile, setPaymentFile] = useState<File | null>(null);
  const [paymentPreviewUrl, setPaymentPreviewUrl] = useState<string | null>(null);
  const [shippingPreviewUrl, setShippingPreviewUrl] = useState<string | null>(null);
  const [copiedTracking, setCopiedTracking] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    // Get current user
    const user = localStorage.getItem('user');
    if (user) {
      try {
        setCurrentUser(JSON.parse(user));
      } catch (e) {
        console.error('Failed to parse user:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchTransaction();
      // Poll every 30 seconds to keep status updated
      const interval = setInterval(() => {
        fetchTransaction(true);
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [id]);

  const fetchTransaction = async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoading(true);
      }
      const response = await transactionService.getTransaction(id!);
      setTransaction(response.data.data);
      setError(null);
    } catch (error: any) {
      console.error('Failed to fetch transaction:', error);
      if (!isBackground) {
        setError(t('transaction.errors.loadFailed'));
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  };

  const handlePaymentFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPaymentFile(file);
      const url = URL.createObjectURL(file);
      setPaymentPreviewUrl(url);
    }
  };

  const handlePaymentRemove = () => {
    setPaymentFile(null);
    if (paymentPreviewUrl) {
      URL.revokeObjectURL(paymentPreviewUrl);
      setPaymentPreviewUrl(null);
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentFile) return;

    try {
      setUploadingPayment(true);
      const formData = new FormData();
      formData.append('paymentSlip', paymentFile);

      await transactionService.uploadPaymentSlip(id!, formData);
      setPaymentFile(null);
      if (paymentPreviewUrl) {
        URL.revokeObjectURL(paymentPreviewUrl);
        setPaymentPreviewUrl(null);
      }
      await fetchTransaction(); // Refresh transaction data
    } catch (error: any) {
      console.error('Failed to upload payment slip:', error);
      setError(error.response?.data?.message || t('transaction.errors.uploadFailed'));
    } finally {
      setUploadingPayment(false);
    }
  };

  const handleShippingFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setShippingFile(file);
      const url = URL.createObjectURL(file);
      setShippingPreviewUrl(url);
    }
  };

  const handleShippingRemove = () => {
    setShippingFile(null);
    if (shippingPreviewUrl) {
      URL.revokeObjectURL(shippingPreviewUrl);
      setShippingPreviewUrl(null);
    }
  };

  const handleShippingSubmit = async () => {
    if (!shippingFile) {
      setError(t('transaction.errors.selectProof'));
      return;
    }

    if (!trackingNumber.trim()) {
      setError(t('transaction.errors.enterTracking'));
      return;
    }

    try {
      setUploadingShipping(true);
      const formData = new FormData();
      formData.append('shippingProof', shippingFile);
      formData.append('shippingTrackingNumber', trackingNumber.trim());
      if (sellerNotes.trim()) {
        formData.append('sellerNotes', sellerNotes.trim());
      }

      await transactionService.uploadShippingProof(id!, formData);
      await transactionService.uploadShippingProof(id!, formData);
      setShippingFile(null);
      if (shippingPreviewUrl) {
        URL.revokeObjectURL(shippingPreviewUrl);
        setShippingPreviewUrl(null);
      }
      setTrackingNumber('');
      setSellerNotes('');
      await fetchTransaction(); // Refresh transaction data
    } catch (error: any) {
      console.error('Failed to upload shipping proof:', error);
      setError(error.response?.data?.message || t('transaction.errors.uploadFailed'));
    } finally {
      setUploadingShipping(false);
    }
  };

  const handleConfirmPayment = async () => {
    try {
      setConfirmingPayment(true);
      await transactionService.confirmPayment(id!);
      await fetchTransaction(); // Refresh transaction data
    } catch (error: any) {
      console.error('Failed to confirm payment:', error);
      setError(error.response?.data?.message || t('transaction.errors.confirmFailed'));
    } finally {
      setConfirmingPayment(false);
    }
  };

  const handleConfirmReceipt = async () => {
    try {
      setConfirmingReceipt(true);
      await transactionService.confirmReceipt(id!);
      await fetchTransaction(); // Refresh transaction data
    } catch (error: any) {
      console.error('Failed to confirm receipt:', error);
      setError(error.response?.data?.message || t('transaction.errors.confirmFailed'));
    } finally {
      setConfirmingReceipt(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!transaction) return;
    try {
      setSubmittingReview(true);
      setReviewError(null);
      await reviewService.createReview(id!, {
        rating: reviewRating,
        comment: reviewComment.trim() || undefined
      });
      setReviewComment('');
      setReviewRating(5);
      await fetchTransaction();
    } catch (error: any) {
      console.error('Failed to submit review:', error);
      setReviewError(error.response?.data?.message || t('transaction.errors.reviewFailed'));
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleCopyTracking = async (trackingNum: string) => {
    try {
      await navigator.clipboard.writeText(trackingNum);
      setCopiedTracking(true);
      setTimeout(() => setCopiedTracking(false), 2000);
    } catch (err) {
      console.error('Failed to copy tracking number:', err);
    }
  };

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= rating ? 'text-amber-500' : 'text-gray-300'}>
          ★
        </span>
      ))}
    </div>
  );

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { bg: string; text: string; label: string }> = {
      PENDING_PAYMENT: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: t('transaction.labels.pendingPayment') },
      PAYMENT_SUBMITTED: { bg: 'bg-blue-100', text: 'text-blue-800', label: t('transaction.labels.paymentSubmitted') },
      PAYMENT_CONFIRMED: { bg: 'bg-green-100', text: 'text-green-800', label: t('transaction.labels.paymentConfirmed') },
      SHIPPED: { bg: 'bg-purple-100', text: 'text-purple-800', label: t('transaction.labels.shipped') },
      COMPLETED: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: t('transaction.labels.completed') },
      CANCELLED: { bg: 'bg-red-100', text: 'text-red-800', label: t('transaction.labels.cancelled') },
    };
    const statusStyle = statusMap[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusStyle.bg} ${statusStyle.text}`}>
        {statusStyle.label}
      </span>
    );
  };

  // Transaction steps for progress indicator
  const transactionSteps = [
    { key: 'PENDING_PAYMENT' },
    { key: 'PAYMENT_SUBMITTED' },
    { key: 'PAYMENT_CONFIRMED' },
    { key: 'SHIPPED' },
    { key: 'COMPLETED' },
  ];

  const parseImages = (imageUrls: string): string[] => {
    try {
      return JSON.parse(imageUrls || '[]');
    } catch {
      return [];
    }
  };

  const parseBundleItems = (raw: any): { name: string; quantity?: number; notes?: string }[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const getCurrentStepIndex = (status: string) => {
    if (status === 'CANCELLED') return -1;
    return transactionSteps.findIndex(step => step.key === status);
  };

  const handleCancelTransaction = async () => {
    if (!window.confirm(t('transaction.cancelConfirm') || 'Are you sure you want to cancel this transaction?')) {
      return;
    }

    try {
      setLoading(true);
      await transactionService.cancelTransaction(id!, 'Cancelled by user');
      await fetchTransaction();
    } catch (error: any) {
      console.error('Failed to cancel transaction:', error);
      setError(error.response?.data?.message || t('transaction.errors.cancelFailed') || 'Failed to cancel transaction');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('notifications.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || t('transaction.errors.notFound')}
          </div>
        </div>
      </div>
    );
  }

  const isBuyer = currentUser?.id === transaction.buyerId;
  const isSeller = currentUser?.id === transaction.sellerId;
  const listingImages = parseImages(transaction.listing.imageUrls);
  const listingPrimaryImage = transaction.listing.card?.imageUrl || listingImages[0];
  const bundleItems = transaction.listing.isBundle ? parseBundleItems(transaction.listing.bundleItems) : [];
  const listingTitle = transaction.listing.isBundle
    ? transaction.listing.bundleName || 'Card Bundle'
    : transaction.listing.card?.name || transaction.listing.customCardName || 'Card';
  const reviews = transaction.reviews || [];
  const myReview = currentUser ? reviews.find((review) => review.reviewerId === currentUser.id) : undefined;
  const counterpartName = isBuyer ? transaction.listing.seller.displayName : transaction.buyer.displayName;
  const counterpartId = isBuyer ? transaction.sellerId : transaction.buyerId;
  const canLeaveReview = transaction.status === 'COMPLETED' && currentUser && (isBuyer || isSeller) && !myReview;

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/marketplace')}
          className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
        >
          {t('transaction.backToMarketplace')}
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">
            {listingTitle} {counterpartName && `(${counterpartName})`} - {new Date(transaction.createdAt).toLocaleDateString()}
          </h1>
          {getStatusBadge(transaction.status)}
        </div>
        <div className="flex justify-between items-center mt-2">
          <p className="text-gray-600">
            {t('transaction.createdOn', { date: new Date(transaction.createdAt).toLocaleString() })}
          </p>
          {transaction.status === 'PENDING_PAYMENT' && (
            <button
              onClick={handleCancelTransaction}
              className="text-red-600 hover:text-red-800 text-sm font-medium border border-red-200 rounded px-3 py-1 bg-red-50 hover:bg-red-100"
            >
              {t('transaction.cancelTransaction') || 'Cancel Transaction'}
            </button>
          )}
        </div>
      </div>

      {/* Transaction Steps Progress Indicator */}
      {transaction.status !== 'CANCELLED' ? (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">{t('transaction.progress')}</h2>
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200">
              <div
                className="h-full bg-blue-600 transition-all duration-500"
                style={{ width: `${(getCurrentStepIndex(transaction.status) / (transactionSteps.length - 1)) * 100}%` }}
              />
            </div>

            {/* Steps */}
            <div className="relative flex justify-between">
              {transactionSteps.map((step, index) => {
                const currentIndex = getCurrentStepIndex(transaction.status);
                const isCompleted = index < currentIndex;
                const isCurrent = index === currentIndex;
                const isPending = index > currentIndex;

                return (
                  <div key={step.key} className="flex flex-col items-center" style={{ width: '20%' }}>
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold z-10
                        ${isCompleted ? 'bg-blue-600 text-white' : ''}
                        ${isCurrent ? 'bg-blue-600 text-white ring-4 ring-blue-200' : ''}
                        ${isPending ? 'bg-gray-200 text-gray-500' : ''}`}
                    >
                      {isCompleted ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                    <p className={`mt-2 text-xs text-center font-medium ${isCurrent ? 'text-blue-600' : 'text-gray-500'}`}>
                      {t(`transaction.steps.${step.key === 'PENDING_PAYMENT' ? 'pendingPayment' :
                        step.key === 'PAYMENT_SUBMITTED' ? 'paymentSubmitted' :
                          step.key === 'PAYMENT_CONFIRMED' ? 'paymentConfirmed' :
                            step.key === 'SHIPPED' ? 'shipped' : 'completed'}`)}
                    </p>
                    {isCurrent && (
                      <p className="text-xs text-gray-400 text-center mt-1 hidden sm:block">
                        {t(`transaction.steps.${step.key === 'PENDING_PAYMENT' ? 'pendingPaymentDesc' :
                          step.key === 'PAYMENT_SUBMITTED' ? 'paymentSubmittedDesc' :
                            step.key === 'PAYMENT_CONFIRMED' ? 'paymentConfirmedDesc' :
                              step.key === 'SHIPPED' ? 'shippedDesc' : 'completedDesc'}`)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-red-800 mb-2">{t('transaction.cancelled')}</h2>
          <p className="text-red-700">{t('transaction.cancelledDesc')}</p>
        </div>
      )}

      {/* Card & Transaction Info */}
      <div className="grid md:grid-cols-2 gap-8 mb-8">
        {/* Card Info */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">
            {transaction.listing.isBundle ? t('transaction.bundleDetails') : t('transaction.cardDetails')}
          </h2>
          <div className="flex gap-4">
            <div className="w-32 h-44 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
              {listingPrimaryImage ? (
                <img
                  src={listingPrimaryImage}
                  alt={listingTitle}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-gray-400 text-4xl">🎴</div>
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold">{listingTitle}</h3>
              {!transaction.listing.isBundle && transaction.listing.card?.cardNumber && (
                <p className="text-gray-600">#{transaction.listing.card.cardNumber}</p>
              )}
              <p className="text-3xl font-bold text-blue-600 mt-4">
                ฿{transaction.agreedPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                {transaction.listing.isBundle && <span className="text-gray-600 text-base font-medium ml-2">{t('transaction.bundleTotal')}</span>}
              </p>
            </div>
          </div>
          {transaction.listing.isBundle && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">{t('transaction.bundleContents')}</p>
              <div className="space-y-2">
                {bundleItems.length > 0 ? (
                  bundleItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-800">
                      <span className="text-gray-500">•</span>
                      <div className="flex-1">
                        <div className="font-medium">
                          {item.quantity ? `${item.quantity}x ` : ''}{item.name}
                        </div>
                        {item.notes && <div className="text-sm text-gray-600">{item.notes}</div>}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-600 text-sm">{t('transaction.notProvided')}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Transaction Parties */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">{t('transaction.transactionInfo')}</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">{t('transaction.buyer')}</p>
              <div className="font-medium text-gray-900 text-lg">
                {transaction.buyer.displayName}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex text-amber-500 text-sm">
                  {renderStars(transaction.buyer.rating || 0)}
                </div>
                <span className="text-sm text-gray-600">
                  ({transaction.buyer.rating?.toFixed(1) || '0.0'})
                </span>
              </div>
              <Link to={`/users/${transaction.buyer.id}/reviews`} className="text-sm text-blue-600 hover:underline mt-1 block">
                {t('transaction.viewReviews')} {transaction.buyer.reviewCount ? `(${transaction.buyer.reviewCount})` : ''}
              </Link>
              {isBuyer && <span className="text-xs text-blue-600 font-medium">{t('transaction.you')}</span>}
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('transaction.seller')}</p>
              <div className="font-medium text-gray-900 text-lg">
                {transaction.listing.seller.displayName}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex text-amber-500 text-sm">
                  {renderStars(transaction.listing.seller.rating || 0)}
                </div>
                <span className="text-sm text-gray-600">
                  ({transaction.listing.seller.rating?.toFixed(1) || '0.0'})
                </span>
              </div>
              <Link to={`/users/${transaction.listing.seller.id}/reviews`} className="text-sm text-blue-600 hover:underline mt-1 block">
                {t('transaction.viewReviews')} {transaction.listing.seller.reviewCount ? `(${transaction.listing.seller.reviewCount})` : ''}
              </Link>
              {isSeller && <span className="text-xs text-blue-600 font-medium">{t('transaction.you')}</span>}
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('transaction.priceBreakdown') || 'Price Breakdown'}</p>
              <div className="flex justify-between items-center text-sm mt-1">
                <span className="text-gray-600">{t('transaction.itemPrice') || 'Item Price'}:</span>
                <span className="font-medium">฿{transaction.agreedPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-sm mt-1">
                <span className="text-gray-600">{t('transaction.shippingCost') || 'Shipping'}:</span>
                <span className="font-medium">฿{(transaction.shippingCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between items-center">
                <span className="font-bold text-gray-900">{t('transaction.total') || 'Total'}:</span>
                <span className="text-2xl font-bold text-blue-600">
                  ฿{((transaction.agreedPrice || 0) + (transaction.shippingCost || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Seller Verification Photos (Unified for WTB & WTS) */}
      {
        (transaction.sellerProofImage || listingImages.length > 0) && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold mb-4">{t('transaction.proofImages', 'Seller Verification Photos')}</h2>
            <p className="text-gray-600 mb-4 text-sm">
              {t('transaction.proofImagesDesc', 'Please verify these photos of the actual item provided by the seller before proceeding with payment.')}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(transaction.sellerProofImage ? parseImages(transaction.sellerProofImage) : listingImages).map((imgUrl, index) => (
                <div key={index} className="aspect-square rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={imgUrl}
                    alt={`Proof ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 cursor-pointer"
                    onClick={() => window.open(imgUrl, '_blank')}
                  />
                </div>
              ))}
            </div>
          </div>
        )
      }

      {/* Payment Section */}
      {
        isBuyer && transaction.status === 'PENDING_PAYMENT' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold mb-4">{t('transaction.uploadPayment')}</h2>
            <p className="text-gray-700 mb-4">
              {t('transaction.uploadPaymentDesc')}
            </p>

            {!paymentFile ? (
              <input
                type="file"
                accept="image/*"
                onChange={handlePaymentFileSelect}
                className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-lg file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
              />
            ) : (
              <div className="space-y-4">
                <div className="relative inline-block">
                  {paymentPreviewUrl && (
                    <img
                      src={paymentPreviewUrl}
                      alt="Payment Slip Preview"
                      className="max-w-xs rounded-lg border border-gray-300"
                    />
                  )}
                  <button
                    onClick={handlePaymentRemove}
                    className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700"
                    title={t('common.remove')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleSubmitPayment}
                    disabled={uploadingPayment}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg disabled:opacity-50"
                  >
                    {uploadingPayment ? t('transaction.uploading') : t('transaction.submitPayment', 'Submit Payment')}
                  </button>
                  <button
                    onClick={handlePaymentRemove}
                    disabled={uploadingPayment}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg"
                  >
                    {t('common.remove', 'Remove')}
                  </button>
                </div>
              </div>
            )}
            {uploadingPayment && <p className="mt-2 text-sm text-gray-600">{t('transaction.uploading')}</p>}
          </div>
        )
      }

      {/* Payment Slip Display */}
      {
        transaction.paymentSlipUrl && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold mb-4">{t('transaction.paymentSlip')}</h2>
            <img
              src={transaction.paymentSlipUrl}
              alt={t('transaction.paymentSlip')}
              className="max-w-md rounded-lg border"
            />
          </div>
        )
      }

      {/* Seller Confirm Payment Section */}
      {
        isSeller && transaction.status === 'PAYMENT_SUBMITTED' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold mb-4">{t('transaction.confirmPayment')}</h2>
            <p className="text-gray-700 mb-4">
              {t('transaction.confirmPaymentDesc')}
            </p>
            <button
              onClick={handleConfirmPayment}
              disabled={confirmingPayment}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirmingPayment ? t('transaction.confirming') : t('transaction.confirmReceived')}
            </button>
          </div>
        )
      }

      {/* Shipping Section - Seller uploads tracking */}
      {
        isSeller && transaction.status === 'PAYMENT_CONFIRMED' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold mb-4">{t('transaction.shipItem')}</h2>
            <p className="text-gray-700 mb-4">
              {t('transaction.shipItemDesc')}
            </p>

            <div className="space-y-4">
              {/* Tracking Number Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('transaction.trackingNumber')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder={t('transaction.enterTracking')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Shipping Proof Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('transaction.shippingProof')} <span className="text-red-500">*</span>
                </label>

                {!shippingFile ? (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleShippingFileSelect}
                    className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                  />
                ) : (
                  <div className="space-y-2">
                    <div className="relative inline-block">
                      {shippingPreviewUrl && (
                        <img
                          src={shippingPreviewUrl}
                          alt="Shipping Proof Preview"
                          className="max-w-xs rounded-lg border border-gray-300"
                        />
                      )}
                      <button
                        onClick={handleShippingRemove}
                        className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700"
                        title={t('common.remove', 'Remove')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div>
                      <button
                        onClick={handleShippingRemove}
                        className="text-sm text-red-600 hover:text-red-800 font-medium"
                      >
                        {t('common.remove', 'Remove')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Seller Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('transaction.notes')}
                </label>
                <textarea
                  value={sellerNotes}
                  onChange={(e) => setSellerNotes(e.target.value)}
                  placeholder={t('transaction.notesPlaceholder')}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Submit Button */}
              <button
                onClick={handleShippingSubmit}
                disabled={uploadingShipping || !trackingNumber.trim() || !shippingFile}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingShipping ? t('transaction.submitting') : t('transaction.submitShipping')}
              </button>
            </div>
          </div>
        )
      }

      {/* Shipping Proof Display */}
      {
        transaction.shippingProofUrl && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-2xl font-bold mb-4">{t('transaction.shippingInfo')}</h2>

            {/* Tracking Number */}
            {transaction.shippingTrackingNumber && (
              <div className="mb-4 p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">{t('transaction.trackingNumber')}</p>
                <div className="flex items-center gap-3">
                  <p className="text-xl font-bold text-purple-700">{transaction.shippingTrackingNumber}</p>
                  <button
                    onClick={() => handleCopyTracking(transaction.shippingTrackingNumber!)}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                  >
                    {copiedTracking ? (
                      <>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {t('transaction.copied')}
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        {t('transaction.copy')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Seller Notes */}
            {transaction.sellerNotes && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">{t('transaction.sellerNotes')}</p>
                <p className="text-gray-800">{transaction.sellerNotes}</p>
              </div>
            )}

            {/* Shipping Proof Image */}
            <div>
              <p className="text-sm text-gray-600 mb-2">{t('transaction.shippingProof')}</p>
              <img
                src={transaction.shippingProofUrl}
                alt={t('transaction.shippingProof')}
                className="max-w-md rounded-lg border"
              />
            </div>
          </div>
        )
      }

      {/* Buyer Confirm Receipt Section */}
      {
        isBuyer && transaction.status === 'SHIPPED' && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold mb-4">{t('transaction.confirmReceipt')}</h2>
            <p className="text-gray-700 mb-4">
              {t('transaction.confirmReceiptDesc')}
            </p>
            <button
              onClick={handleConfirmReceipt}
              disabled={confirmingReceipt}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirmingReceipt ? t('transaction.confirming') : t('transaction.confirmItemReceived')}
            </button>
          </div>
        )
      }

      {/* Reviews */}
      {
        transaction.status === 'COMPLETED' && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold mb-2">{t('transaction.reviews')}</h2>
                <p className="text-gray-600">{t('transaction.reviewsDesc')}</p>
              </div>
              <Link
                to={`/users/${counterpartId}/reviews`}
                className="text-blue-700 hover:text-blue-800 font-semibold"
              >
                {t('transaction.viewAllReviews', { name: counterpartName })}
              </Link>
            </div>

            <div className="mt-6">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                {canLeaveReview ? (
                  <>
                    <p className="text-sm font-semibold text-blue-900 mb-2">{t('transaction.rate', { name: counterpartName })}</p>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setReviewRating(star)}
                            className={`text-2xl ${star <= reviewRating ? 'text-amber-500' : 'text-gray-300'}`}
                            aria-label={`Rate ${star} star${star === 1 ? '' : 's'}`}
                            type="button"
                          >
                            ★
                          </button>
                        ))}
                      </div>
                      <span className="text-sm text-gray-700">{reviewRating} / 5</span>
                    </div>
                    <textarea
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      rows={4}
                      placeholder={t('transaction.ratePlaceholder', { name: counterpartName })}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                    {reviewError && <p className="text-sm text-red-600 mt-2">{reviewError}</p>}
                    <button
                      onClick={handleSubmitReview}
                      disabled={submittingReview}
                      className="mt-3 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50"
                    >
                      {submittingReview ? t('transaction.submitting') : t('transaction.postReview', { name: counterpartName })}
                    </button>
                  </>
                ) : (
                  <div className="space-y-2">
                    <p className="text-lg font-semibold text-blue-900">{t('transaction.feedbackLocked')}</p>
                    {myReview ? (
                      <>
                        <div className="flex items-center gap-2">
                          {renderStars(myReview.rating)}
                          <span className="font-semibold text-gray-800">{myReview.rating}/5</span>
                        </div>
                        {myReview.comment && <p className="text-gray-700">{myReview.comment}</p>}
                        <p className="text-sm text-gray-600">{t('transaction.thanks')}</p>
                      </>
                    ) : (
                      <p className="text-gray-700">{t('transaction.needComplete')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Transaction Chat */}
      {
        currentUser && (
          <TransactionChat
            transactionId={id!}
            currentUserId={currentUser.id}
            otherUserName={isBuyer ? transaction.listing.seller.displayName : transaction.buyer.displayName}
          />
        )
      }
    </div>
  );
};

export default TransactionDetailPage;
